// Analysis Detail Route - Enhanced with full extraction & report display
import React, { useMemo, useState, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { OverrideAuditDisplay } from '@/components/OverrideAuditDisplay';
import { formatDate, formatNumber, formatPercent, cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  FileText,
  Download,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  CreditCard,
  Building2,
  Banknote,
  Calculator,
  FileBarChart,
  Info,
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Loader2,
  Save,
  Globe,
  ExternalLink,
  Search,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
export const Route = createFileRoute('/analyses/$id')({
  component: AnalysisDetail,
});
import { SaveTemplateDialog } from '@/components/templates';

// ===========================================
// Universal safe-value renderer
// Converts any value to a human-readable string/element.
// Prevents [object Object] from ever appearing in the UI.
// ===========================================

/** Format a camelCase or snake_case key into a readable label */
const humanLabel = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

/**
 * Recursively render any value as a React node.
 * - primitives → string
 * - arrays of primitives → comma-joined
 * - arrays of objects → bullet list with key-value pairs
 * - plain objects → comma-joined "Key: Value" pairs
 */
function SafeValue({ value }: { value: unknown }): React.ReactElement {
  if (value === null || value === undefined || value === '')
    return <span className="text-muted-foreground">N/A</span>;

  if (typeof value === 'boolean')
    return <span>{value ? 'Yes' : 'No'}</span>;

  if (typeof value === 'number' || typeof value === 'string')
    return <span>{String(value)}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground">N/A</span>;
    // Array of primitives
    if (value.every((v) => typeof v !== 'object' || v === null))
      return <span>{value.map((v) => String(v ?? 'N/A')).join(', ')}</span>;
    // Array of objects
    return (
      <div className="space-y-2 mt-1">
        {value.map((item, i) => (
          <div key={i} className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            {typeof item === 'object' && item !== null ? (
              Object.entries(item as Record<string, unknown>).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-0.5">
                  <span className="text-muted-foreground">{humanLabel(k)}</span>
                  <span className="font-medium text-right"><SafeValue value={v} /></span>
                </div>
              ))
            ) : (
              <span>{String(item)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== '',
    );
    if (entries.length === 0) return <span className="text-muted-foreground">N/A</span>;
    // If only a few simple entries, render inline
    if (entries.length <= 4 && entries.every(([, v]) => typeof v !== 'object'))
      return (
        <span>
          {entries.map(([k, v]) => `${humanLabel(k)}: ${String(v ?? 'N/A')}`).join(', ')}
        </span>
      );
    // Otherwise render as a mini card
    return (
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm mt-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 py-0.5">
            <span className="text-muted-foreground">{humanLabel(k)}</span>
            <span className="font-medium text-right"><SafeValue value={v} /></span>
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(value)}</span>;
}

// ===========================================
// Main Component
// ===========================================

function AnalysisDetail() {
  const { id } = Route.useParams();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  // Determine if the id is a UUID or a human-readable reportId
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  // Try to reuse a previously generated report saved to sessionStorage
  const initialAnalysis = useMemo(() => {
    try {
      const stored = sessionStorage.getItem(`analysis:${id}`);
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      return null;
    }
  }, [id]);

  // Clear workflow session data when landing on a completed analysis.
  // The workflow for this tab is done — clearing sessionStorage allows
  // the user to start a fresh workflow in the same tab.
  React.useEffect(() => {
    const STORAGE_KEY = 'creditnova_workflow_data';
    const STEP_STORAGE_KEY = 'creditnova_workflow_step';
    const COMPLETED_STORAGE_KEY = 'creditnova_workflow_completed';
    try {
      const persisted = sessionStorage.getItem(STORAGE_KEY);
      if (persisted) {
        const parsed = JSON.parse(persisted);
        // Only clear if the workflow is complete (avoid clearing mid-workflow
        // if the user navigated here from the analyses list, not from a
        // just-finished report generation).
        if (parsed.workflowState === 'complete') {
          sessionStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STEP_STORAGE_KEY);
          sessionStorage.removeItem(COMPLETED_STORAGE_KEY);
        }
      }
    } catch { /* ignore */ }
  }, [id]);

  // Clean up sessionStorage when leaving this page (security)
  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem(`analysis:${id}`);
    };
  }, [id]);

  const { data: analysis, isLoading, error } = useQuery({
    queryKey: ['analysis', id],
    queryFn: () => isUUID ? api.analyses.get(id) : api.analyses.getByReportId(id),
    // This allows the query to stay in isFetching state while showing the UI.
    placeholderData: initialAnalysis || undefined,
    
    // considered it stale immeidately so it fetches the DB version ( analysis record from Analyses table, which is the source of truth) even if placeholder exists.
    staleTime: 0,
    
    refetchInterval: (query) => {
      const data = query.state.data;
      // Keep polling if we don't have data yet or if it's still being processed
      if (!data || data.status === 'in_progress') return 3000;
      return false;
    }
  });

  const mappedWorkflowData = useMemo(() => {
    if (!analysis) return null;

    const ai = analysis.aiAnalysis || {};
    const raw = ai.rawData || {};
    const findings = ai.findings || {};

    // 1. Determine if Corporate data exists in any form
    const hasCorporate = !!(raw.accountData || (findings.accountItems && findings.accountItems.length > 0));

    // 2. Reconstruct the summaryPreferences to match the "Report Step" version.
    //    Prefer the stored preferences from aiAnalysis (persisted at report
    //    generation time) so that templates saved from this page inherit the
    //    original summaryStyle, summaryLength, and customInstructions.
    //    Fall back to sensible defaults for analyses created before this field
    //    was persisted.
    const stored = ai.summaryPreferences || {};
    const summaryPreferences = {
      writeupType: analysis.writeupType || ai.summary?.writeupType || 'full_analysis',
      summaryStyle: stored.summaryStyle || 'comprehensive',
      summaryLength: stored.summaryLength || 'standard',
      customInstructions: stored.customInstructions || '',
      bankStatementType: stored.bankStatementType || 'business',
      financialStatementType: stored.financialStatementType || 'interim_fs',
      selectedDataPoints: {
        financialStatements: stored.selectedDataPoints?.financialStatements
          || (raw.financialData ? ["All Income Statement Items", "All Balance Sheet Items"] : []),
        creditReport: stored.selectedDataPoints?.creditReport
          || ((findings.creditReportItems || findings.equifaxItems)?.length > 0 ? ["Credit Score", "Payment History"] : []),
        metrics: stored.selectedDataPoints?.metrics
          || (raw.selectedMetrics || []),
        // map the corporateSearch data points so the template logic sees it as "enabled"
        corporateSearch: stored.selectedDataPoints?.corporateSearch
          || (hasCorporate
            ? ["Company Name", "Business Number", "Registration Date", "Business Type", "Registered Address", "Directors & Officers", "Share Structure", "Business Activities", "All Corporate Information"]
            : []),
        bankStatements: stored.selectedDataPoints?.bankStatements
          || (raw.bankStatementFindings ? ["Revenue (Deposits)", "Expense Management (Debits)"] : []),
      }
    };

    // 3. Return the object structured exactly like the Workflow Context 'data'
    return {
      ...analysis,
      businessName: analysis.businessName,
      // Map extracted data keys to the naming convention buildTemplateConfig expects
      financialData: raw.financialData,
      bankStatementFindings: raw.bankStatementFindings,
      accountData: raw.accountData, // buildTemplateConfig checks this for modules.corporate.enabled
      creditReportData: raw.creditReportData || raw.equifaxData,

      // Map selections (with fallback reads for legacy field names)
      selectedMetrics: raw.selectedMetrics || [],
      customRatios: raw.customRatios || [],
      offBalanceSheetRecords: raw.offBalanceSheetRecords || {},
      selectedCreditReportItems: raw.selectedCreditReportItems || raw.selectedEquifaxItems || findings.creditReportItems || findings.equifaxItems || [],

      // Pass the reconstructed preferences
      summaryPreferences
    };
  }, [analysis]);

  // Handle PDF export - simple call to backend which handles everything
  const handleExportPdf = async () => {
    if (!analysis) return;

    setIsExporting(true);
    toast({
      title: 'Generating PDF',
      description: 'Please wait while we create your PDF report...',
    });

    try {
      // Just pass the ID - backend fetches data and generates PDF
      const pdfBuffer = await api.analyses.exportPdf(analysis.id);

      // Create a Blob and trigger download
      const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const sanitizedName = (analysis.businessName || 'Credit_Report').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `CreditNova_${sanitizedName}_${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'PDF Downloaded',
        description: 'Your credit analysis report has been saved as PDF',
      });
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-64 mb-4" />
        <div className="skeleton h-4 w-48 mb-8" />
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
        <h1 className="text-2xl font-bold mb-2">Analysis Not Found</h1>
        <p className="text-muted-foreground mb-6">The analysis you're looking for doesn't exist.</p>
        <Link
          to="/analyses"
          className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Analyses
        </Link>
      </div>
    );
  }

  const statusColors = {
    draft: 'bg-muted text-muted-foreground',
    in_progress: 'bg-warning/10 text-warning',
    completed: 'bg-success/10 text-success',
    archived: 'bg-muted text-muted-foreground',
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <Link
            to="/analyses"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analyses
          </Link>
          <h1 className="text-3xl font-bold">
            {analysis.businessName || 'Untitled Analysis'}
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-muted-foreground">Report ID: {analysis.reportId}</span>
            <span
              className={cn(
                'px-3 py-1 text-sm font-medium rounded-full capitalize',
                statusColors[analysis.status as keyof typeof statusColors]
              )}
            >
              {(analysis.status || '').replace('_', ' ')}
            </span>
          </div>
        </div>

        <div className="flex gap-3">
          {analysis.status === 'in_progress' && (
            <Button disabled className="gap-2">
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              Analyzing...
            </Button>
          )}
          {(analysis.status === 'complete' || analysis.status === 'completed') && (
            <SaveTemplateDialog
              workflowData={mappedWorkflowData as any} // Pass the fetched analysis as the context source
              trigger={
                <Button variant="outline" disabled={isExporting}>
                  <Save className="w-5 h-5 mr-2" />
                  Save as Template
                </Button>
              }
            />
          )}
          {(analysis.status === 'complete' || analysis.status === 'completed') && (
            <Button onClick={handleExportPdf} disabled={isExporting}>
              {isExporting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Download className="w-5 h-5 mr-2" />
              )}
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          )}
        </div>
      </div>


      {/* Enhanced Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="flex flex-wrap gap-2 h-auto p-2 bg-muted rounded-xl">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <FileBarChart className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="financials" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Financials
          </TabsTrigger>
          <TabsTrigger value="ratios" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Ratios
          </TabsTrigger>
          <TabsTrigger value="credit-report" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Credit Report
          </TabsTrigger>
          <TabsTrigger value="bank" className="flex items-center gap-2">
            <Banknote className="w-4 h-4" />
            Bank Statements
          </TabsTrigger>
          <TabsTrigger value="corporate" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Corporate
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            AI Analysis
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="overrides" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Overrides
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="financials">
          <FinancialsTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="ratios">
          <RatiosTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="credit-report">
          <CreditReportTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="bank">
          <BankStatementTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="corporate">
          <CorporateTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="ai">
          <AIAnalysisTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsTab analysis={analysis} />
        </TabsContent>

        <TabsContent value="overrides">
          <OverridesTab workflowId={analysis.workflowId} analysisId={analysis.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===========================================
// Overview Tab
// ===========================================

function OverviewTab({ analysis }: { analysis: any }) {
  // Get ratios from either array format (new) or nested format (legacy)
  const ratiosArray = analysis.aiAnalysis?.findings?.financialRatios  
    || analysis.calculatedRatios 
    || [];
  
  // Helper to find a ratio by name pattern
  const findRatio = (pattern: string) => {
    if (Array.isArray(ratiosArray)) {
      const ratio = ratiosArray.find((r: any) => 
        r.ratioName?.toLowerCase().includes(pattern.toLowerCase())
      );
      return ratio?.calculatedValue || null;
    }
    return null;
  };

  const hasRatios = Array.isArray(ratiosArray) && ratiosArray.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Summary Card */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Summary</h3>
        <div className="space-y-4">
          <InfoRow label="Business Name" value={analysis.businessName || 'N/A'} />
          <InfoRow label="Report ID" value={analysis.reportId} />
          <InfoRow label="Status" value={analysis.status} />
          <InfoRow label="Created" value={formatDate(analysis.createdAt)} />
          <InfoRow label="Last Updated" value={formatDate(analysis.updatedAt)} />
        </div>
      </Card>

      {/* Quick Metrics */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Key Metrics</h3>
        {hasRatios ? (
          <div className="grid grid-cols-2 gap-4">
            {/* Show first 4 ratios from the analysis */}
            {ratiosArray.slice(0, 4).map((ratio: any, i: number) => (
              <div key={i} className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">{ratio.ratioName}</p>
                <p className="text-2xl font-bold mt-1">{ratio.calculatedValue || 'N/A'}</p>
              </div>
            ))}
            {ratiosArray.length === 0 && (
              <>
                <MetricCard
                  label="Current Ratio"
                  value={findRatio('current ratio')}
                  benchmark={1.5}
                />
                <MetricCard
                  label="Debt to Equity"
                  value={findRatio('debt to equity') || findRatio('debt/equity')}
                  benchmark={2.0}
                  inverse
                />
                <MetricCard
                  label="Net Margin"
                  value={findRatio('net margin') || findRatio('net profit margin')}
                  benchmark={5}
                  suffix="%"
                />
                <MetricCard
                  label="Interest Coverage"
                  value={findRatio('interest coverage')}
                  benchmark={3.0}
                />
              </>
            )}
          </div>
        ) : (
          //Modified the message so that we dont' confuse users when they haven't selected any metrics during the analysis creation. This way they know why no metrics are showing up here.S
          <p className="text-muted-foreground">
            No metrics were selected during the Financial Statement Analysis in this workflow.
          </p>
        )}
      </Card>

      {/* AI Summary */}
      {analysis.aiAnalysis?.executiveSummary && (
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-semibold mb-4">Executive Summary</h3>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
            {analysis.aiAnalysis.executiveSummary}
          </p>

          {analysis.aiAnalysis.creditDecision && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-3">
                {analysis.aiAnalysis.creditDecision.recommendation === 'approve' ? (
                  <CheckCircle className="w-6 h-6 text-success" />
                ) : analysis.aiAnalysis.creditDecision.recommendation === 'decline' ? (
                  <AlertCircle className="w-6 h-6 text-destructive" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-warning" />
                )}
                <div>
                  <p className="font-semibold capitalize">
                    {analysis.aiAnalysis.creditDecision.recommendation}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {analysis.aiAnalysis.creditDecision.rationale}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ===========================================
// Financials Tab - Shows extracted financial statement data
// ===========================================

function FinancialsTab({ analysis }: { analysis: any }) {
  // Check for multi-document extractions
  const allExtractions = analysis.aiAnalysis?.allFinancialExtractions as
    | Array<{ data: any; period?: string; documentId?: string }>
    | undefined;
  const hasMultipleDocs = allExtractions && allExtractions.length > 1;

  if (hasMultipleDocs) {
    return (
      <Tabs defaultValue="doc-0" className="space-y-4">
        <TabsList>
          {allExtractions.map((ext, i) => (
            <TabsTrigger key={i} value={`doc-${i}`}>
              {ext.period || `Document ${i + 1}`}
            </TabsTrigger>
          ))}
        </TabsList>
        {allExtractions.map((ext, i) => (
          <TabsContent key={i} value={`doc-${i}`}>
            <FinancialsTabContent rawFinancialData={ext.data} />
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  // Single document fallback (existing behavior)
  const rawFinancialData = analysis.aiAnalysis?.rawData?.financialData
    || analysis.rawData?.financialData
    || analysis.financialData;

  return <FinancialsTabContent rawFinancialData={rawFinancialData} />;
}

function FinancialsTabContent({ rawFinancialData }: { rawFinancialData: any }) {
  if (!rawFinancialData) {
    return (
      <Card className="p-16 text-center">
        <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No Financial Data</p>
        <p className="text-muted-foreground">
          Financial statement data will appear here after extraction.
        </p>
      </Card>
    );
  }

  // Handle nested data structure - financialData.data.incomeStatement vs financialData.incomeStatement
  const financialData = rawFinancialData.data || rawFinancialData;
  const metadata = rawFinancialData.metadata || {
    companyName: rawFinancialData.companyName,
    periodEnding: rawFinancialData.periodEnding,
    detailLevel: rawFinancialData.detailLevel,
  };
  const notes = financialData.notes || rawFinancialData.notes;

  // Check if we have any actual statement data
  const hasStatements = financialData.incomeStatement || financialData.balanceSheet || financialData.cashFlow;

  if (!hasStatements) {
    return (
      <Card className="p-16 text-center">
        <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No Financial Statements</p>
        <p className="text-muted-foreground">
          Financial statement data was not extracted. Please check if financial statements were uploaded.
        </p>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Document Metadata */}
        {(metadata.companyName || metadata.periodEnding) && (
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Document Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metadata.companyName && (
                <div>
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium">{metadata.companyName}</p>
                </div>
              )}
              {metadata.periodEnding && (
                <div>
                  <p className="text-sm text-muted-foreground">Period Ending</p>
                  <p className="font-medium">{metadata.periodEnding}</p>
                </div>
              )}
              {metadata.detailLevel && (
                <div>
                  <p className="text-sm text-muted-foreground">Detail Level</p>
                  <p className="font-medium capitalize">{metadata.detailLevel}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Statement Tabs */}
        <Tabs defaultValue="income" className="space-y-4">
          <TabsList>
            <TabsTrigger value="income">Income Statement</TabsTrigger>
            <TabsTrigger value="balance">Balance Sheet</TabsTrigger>
            <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
            {notes?.length > 0 && (
              <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="income">
            <FinancialStatementSection
              data={financialData.incomeStatement}
              title="Income Statement"
              notes={notes}
            />
          </TabsContent>

          <TabsContent value="balance">
            <FinancialStatementSection
              data={financialData.balanceSheet}
              title="Balance Sheet"
              notes={notes}
            />
          </TabsContent>

          <TabsContent value="cashflow">
            <FinancialStatementSection
              data={financialData.cashFlow || financialData.cashFlowStatement}
              title="Cash Flow Statement"
              notes={notes}
            />
          </TabsContent>

          {notes?.length > 0 && (
            <TabsContent value="notes">
              <NotesSection notes={notes} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

function FinancialStatementSection({
  data,
  title,
  notes,
}: {
  data: Record<string, any> | null;
  title: string;
  notes?: any[];
}) {
  // Create a map of line items to their notes for quick lookup (matching ExtractionResults.tsx)
  const noteMap = useMemo(() => {
    const map = new Map<string, any>();
    if (notes) {
      notes.forEach((note: any) => {
        if (note.lineItem) {
          map.set(note.lineItem, note);
        }
      });
    }
    return map;
  }, [notes]);

  if (!data) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">No {title} data available.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h4 className="font-semibold mb-4">{title}</h4>
      <ScrollArea className="h-[500px]">
        <div className="space-y-2 pr-4">
          {Object.entries(data).map(([key, value]) => (
            <DataRow key={key} label={key} value={value} note={noteMap.get(key)} />
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}

function DataRow({
  label,
  value,
  note,
}: {
  label: string;
  value: any;
  note?: any;
}) {
  const formattedLabel = label
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  // Get tooltip content from note (matches ExtractionResults.tsx logic)
  const getTooltipContent = (note: any): string => {
    // Use structured content if available
    if (note.content && Array.isArray(note.content) && note.content.length > 0) {
      // Get only text blocks for tooltip (tables don't display well)
      const textBlocks = note.content
        .filter((block: any) => block.type === 'text')
        .map((block: any) => block.value)
        .join(' ');
      
      // Truncate to reasonable length for tooltip
      if (textBlocks.length > 300) {
        return textBlocks.substring(0, 300).trim() + '...';
      }
      return textBlocks || note.description || '';
    }
    
    // Fallback to description, truncated
    const desc = note.description || '';
    if (desc.length > 300) {
      return desc.substring(0, 300).trim() + '...';
    }
    return desc;
  };

  // Check if note has tables
  const hasTable = note?.content?.some((block: any) => block.type === 'table');

  // For the inline display text, flatten objects to readable strings
  const displayValue = typeof value === 'number'
    ? `$${formatNumber(value, 0)}`
    : typeof value === 'string'
    ? value
    : typeof value === 'object' && value !== null
    ? (Array.isArray(value)
        ? value.map((v) => (typeof v === 'object' && v !== null
            ? Object.entries(v).filter(([,val]) => val != null && val !== '').map(([k,val]) => `${humanLabel(k)}: ${String(val)}`).join(', ')
            : String(v))).join('; ')
        : Object.entries(value as Record<string, unknown>).filter(([,v]) => v != null && v !== '').map(([k,v]) => `${humanLabel(k)}: ${String(v)}`).join(', '))
    : String(value ?? 'N/A');

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-b-0">
      <span className="text-sm font-medium text-muted-foreground">{formattedLabel}</span>
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold font-mono">{displayValue}</span>
        {note && (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <button 
                className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
              >
                <Info className="w-3 h-3 text-primary" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-sm">
              <div className="space-y-1">
                {note.noteReference && (
                  <p className="text-xs font-semibold text-primary">{note.noteReference}</p>
                )}
                {note.noteTitle && (
                  <p className="text-xs font-medium">{note.noteTitle}</p>
                )}
                <p className="text-sm text-muted-foreground">{getTooltipContent(note)}</p>
                {hasTable && (
                  <p className="text-xs text-muted-foreground italic">View note for full details including tables</p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ===========================================
// Note Table Renderer - Renders tables from note content
// ===========================================

function NoteTableRenderer({ table }: { table: any }) {
  // Build a 2D grid from the cells array
  const grid = useMemo(() => {
    const result: string[][] = Array(table.rowCount)
      .fill(null)
      .map(() => Array(table.columnCount).fill(''));
    
    for (const cell of table.cells) {
      if (cell.rowIndex < table.rowCount && cell.columnIndex < table.columnCount) {
        result[cell.rowIndex][cell.columnIndex] = cell.content.trim();
      }
    }
    
    return result;
  }, [table]);

  if (grid.length === 0) return null;

  return (
    <div className="overflow-x-auto my-3">
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-border">
            {grid[0].map((cell, colIdx) => (
              <th 
                key={colIdx}
                className={`py-2 px-3 font-semibold text-foreground ${
                  colIdx === 0 ? 'text-left' : 'text-right'
                }`}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.slice(1).map((row, rowIdx) => (
            <tr 
              key={rowIdx} 
              className="border-b border-border/50 hover:bg-muted/30 transition-colors"
            >
              {row.map((cell, colIdx) => (
                <td 
                  key={colIdx}
                  className={`py-2 px-3 ${
                    colIdx === 0 
                      ? 'text-left text-muted-foreground' 
                      : 'text-right font-mono'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================
// Note Content Renderer - Renders text and tables with truncation
// ===========================================

function NoteContentRenderer({ 
  content, 
  isExpanded, 
  charLimit 
}: { 
  content: any[]; 
  isExpanded: boolean; 
  charLimit: number;
}) {
  // Calculate total text length to determine if truncation is needed
  const totalTextLength = useMemo(() => {
    return content
      .filter(block => block.type === 'text')
      .reduce((sum: number, block: any) => sum + (block.value?.length || 0), 0);
  }, [content]);

  const shouldTruncate = !isExpanded && totalTextLength > charLimit;
  let remainingChars = charLimit;

  return (
    <div className="space-y-3">
      {content.map((block, idx) => {
        if (block.type === 'text') {
          const text = block.value || '';
          
          if (shouldTruncate) {
            if (remainingChars <= 0) {
              return null; // Already hit limit, skip this block
            }
            
            if (text.length > remainingChars) {
              const truncatedText = text.substring(0, remainingChars) + '...';
              remainingChars = 0;
              return (
                <p key={idx} className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {truncatedText}
                </p>
              );
            }
            
            remainingChars -= text.length;
          }
          
          return (
            <p key={idx} className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {text}
            </p>
          );
        }
        
        if (block.type === 'table') {
          // Always show tables if we haven't hit the char limit yet
          if (shouldTruncate && remainingChars <= 0) {
            return null;
          }
          
          return <NoteTableRenderer key={idx} table={block.value} />;
        }
        
        return null;
      })}
    </div>
  );
}

// ===========================================
// Expandable Note Card Component
// ===========================================

function ExpandableNoteCard({ note }: { note: any }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const CHAR_LIMIT = 400;
  
  // Check if we have content blocks (new format) or just description (legacy)
  const hasContentBlocks = note.content && Array.isArray(note.content) && note.content.length > 0;
  const description = note.description || '';
  
  // Calculate if truncation is needed
  const isTruncated = useMemo(() => {
    if (hasContentBlocks) {
      const totalTextLength = note.content
        .filter((block: any) => block.type === 'text')
        .reduce((sum: number, block: any) => sum + (block.value?.length || 0), 0);
      return totalTextLength > CHAR_LIMIT;
    }
    return description.length > CHAR_LIMIT;
  }, [hasContentBlocks, note.content, description]);

  return (
    <Card 
      className="border-l-4 border-l-primary hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => isTruncated && setIsExpanded(!isExpanded)}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {note.noteReference && (
                <Badge variant="outline" className="text-xs font-semibold">
                  {note.noteReference}
                </Badge>
              )}
              <h5 className="font-semibold text-sm">
                {note.noteTitle || note.lineItem}
              </h5>
            </div>
            {note.lineItem && note.noteTitle && (
              <p className="text-xs text-muted-foreground">
                Line Item: {note.lineItem}
              </p>
            )}
            {note.pageNumbers?.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Page {note.pageNumbers.join(', ')}
              </p>
            )}
          </div>
          {isTruncated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="shrink-0"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Read More
                </>
              )}
            </Button>
          )}
        </div>
        
        <div className="pt-2">
          {hasContentBlocks ? (
            // New format: render content blocks with proper tables
            <NoteContentRenderer 
              content={note.content} 
              isExpanded={isExpanded}
              charLimit={CHAR_LIMIT}
            />
          ) : (
            // Legacy format: render plain text description
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {isExpanded || !isTruncated 
                ? description 
                : description.substring(0, CHAR_LIMIT) + '...'}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

// ===========================================
// Notes Section Component
// ===========================================

function NotesSection({ notes }: { notes: any[] }) {
  return (
    <div className="space-y-4">
      <h4 className="font-semibold">Financial Statement Notes ({notes.length})</h4>
      <div className="grid gap-4">
        {notes.map((note, index) => (
          <ExpandableNoteCard key={index} note={note} />
        ))}
      </div>
    </div>
  );
}

// ===========================================
// Ratios Tab
// ===========================================

function RatiosTab({ analysis }: { analysis: any }) {
  const ratios = analysis.calculatedRatios;
  // Report ratios can be in aiAnalysis.findings or directly in findings
  const reportRatios = analysis.aiAnalysis?.findings?.financialRatios 
    || analysis.findings?.financialRatios 
    || analysis.calculatedRatios;

  if (!ratios && !reportRatios) {
    return (
      <Card className="p-16 text-center">
        <Calculator className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No ratios calculated</p>
        <p className="text-muted-foreground">Run the analysis to generate financial ratios.</p>
      </Card>
    );
  }

  // If we have detailed report ratios, show those
  if (reportRatios?.length > 0) {
    const categories = [...new Set(reportRatios.map((r: any) => r.category))];

    return (
      <div className="space-y-6">
        {categories.map((category) => (
          <Card key={category as string} className="p-6">
            <h3 className="font-semibold mb-4 text-primary">{category as string} Ratios</h3>
            <div className="space-y-4">
              {reportRatios
                .filter((r: any) => r.category === category)
                .map((ratio: any, idx: number) => (
                  <div key={idx} className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{ratio.ratioName}</span>
                      <span className="text-2xl font-bold text-primary">
                        {ratio.calculatedValue}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">Formula:</span> {ratio.formula}
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">Calculation:</span> {ratio.calculation}
                    </p>
                    <p className="text-sm">{ratio.interpretation}</p>
                    {ratio.sources?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          Sources: {ratio.sources.map((s: any) => s.section).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </Card>
        ))}
      </div>
    );
  }

  // Fallback to basic ratios display
  // Added a message here to clarify why no ratios are showing up in case users didn't select any during analysis creation, so they understand it's not an error but just a missing selection.
  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground">
        No Ratios were selected during the Financial Statement Analysis in this workflow.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold mb-4 text-primary">Liquidity Ratios</h3>
          <div className="space-y-4">
            <RatioRow label="Current Ratio" value={ratios?.liquidity?.currentRatio} benchmark="≥ 1.5" />
            <RatioRow label="Quick Ratio" value={ratios?.liquidity?.quickRatio} benchmark="≥ 1.0" />
            <RatioRow label="Working Capital" value={ratios?.liquidity?.workingCapital} format="currency" />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4 text-primary">Leverage Ratios</h3>
          <div className="space-y-4">
            <RatioRow label="Debt to Equity" value={ratios?.leverage?.debtToEquity} benchmark="≤ 2.0" />
            <RatioRow label="D/TNW" value={ratios?.leverage?.debtToTangibleNetWorth} benchmark="≤ 3.0" />
            <RatioRow label="Debt Ratio" value={ratios?.leverage?.debtRatio} format="percent" benchmark="≤ 60%" />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4 text-primary">Profitability Ratios</h3>
          <div className="space-y-4">
            <RatioRow label="Gross Margin" value={ratios?.profitability?.grossProfitMargin} format="percent" />
            <RatioRow label="Net Margin" value={ratios?.profitability?.netProfitMargin} format="percent" benchmark="≥ 5%" />
            <RatioRow label="ROE" value={ratios?.profitability?.returnOnEquity} format="percent" benchmark="≥ 15%" />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4 text-primary">Coverage Ratios</h3>
          <div className="space-y-4">
            <RatioRow label="Interest Coverage" value={ratios?.coverage?.interestCoverage} benchmark="≥ 3.0" />
            <RatioRow label="DSCR" value={ratios?.coverage?.debtServiceCoverage} benchmark="≥ 1.25" />
          </div>
        </Card>
      </div>
    </div>
    
  );
}

// ===========================================
// Credit Report Tab
// ===========================================

function CreditReportTab({ analysis }: { analysis: any }) {
  // Check for multi-document extractions
  const allExtractions = analysis.aiAnalysis?.allCreditReportExtractions as
    | Array<{ data: any; period?: string; documentId?: string }>
    | undefined;
  const hasMultipleDocs = allExtractions && allExtractions.length > 1;

  if (hasMultipleDocs) {
    return (
      <Tabs defaultValue="doc-0" className="space-y-4">
        <TabsList>
          {allExtractions.map((ext, i) => (
            <TabsTrigger key={i} value={`doc-${i}`}>
              {ext.period || `Report ${i + 1}`}
            </TabsTrigger>
          ))}
        </TabsList>
        {allExtractions.map((ext, i) => (
          <TabsContent key={i} value={`doc-${i}`}>
            <CreditReportTabContent creditReportData={ext.data} />
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  // Single document fallback (existing behavior)
  const creditReportData = analysis.aiAnalysis?.rawData?.creditReportData || analysis.aiAnalysis?.rawData?.equifaxData
    || analysis.rawData?.creditReportData || analysis.rawData?.equifaxData
    || analysis.creditReportData || analysis.equifaxData;

  return <CreditReportTabContent creditReportData={creditReportData} />;
}

function CreditReportTabContent({ creditReportData }: { creditReportData: any }) {
  if (!creditReportData) {
    return (
      <Card className="p-16 text-center">
        <CreditCard className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No Credit Report Data</p>
        <p className="text-muted-foreground">Credit report data will appear here after extraction.</p>
      </Card>
    );
  }

  const data = creditReportData?.data || {};
  const categories = ['personalInfo', 'creditSummary', 'accountHistory', 'inquiries', 'publicRecords'];

  return (
    <Tabs defaultValue="personalInfo" className="space-y-4">
      <TabsList>
        <TabsTrigger value="personalInfo">Personal Info</TabsTrigger>
        <TabsTrigger value="creditSummary">Credit Summary</TabsTrigger>
        <TabsTrigger value="accountHistory">Accounts</TabsTrigger>
        <TabsTrigger value="inquiries">Inquiries</TabsTrigger>
        <TabsTrigger value="publicRecords">Public Records</TabsTrigger>
      </TabsList>

      {categories.map((category) => (
        <TabsContent key={category} value={category}>
          <Card className="p-6">
            <h4 className="font-semibold mb-4 capitalize">
              {category.replace(/([A-Z])/g, ' $1').trim()}
            </h4>
            {data[category] ? (
              Array.isArray(data[category]) ? (
                <div className="space-y-4">
                  {data[category].map((item: any, idx: number) => (
                    <div key={idx} className="p-4 bg-muted rounded-lg">
                      {Object.entries(item).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-1 gap-4">
                          <span className="text-muted-foreground capitalize shrink-0">
                            {humanLabel(key)}
                          </span>
                          <div className="font-medium text-right"><SafeValue value={value} /></div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data[category]).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-2 border-b border-border/50 gap-4">
                      <span className="text-muted-foreground capitalize shrink-0">
                        {humanLabel(key)}
                      </span>
                      <div className="font-medium text-right"><SafeValue value={value} /></div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="text-muted-foreground">No data available for this category.</p>
            )}
          </Card>
        </TabsContent>
      ))}
    </Tabs>
  );
}

// ===========================================
// Bank Statement Tab
// ===========================================

function BankStatementTab({ analysis }: { analysis: any }) {
  // Check for multi-document extractions
  const allExtractions = analysis.aiAnalysis?.allBankStatementExtractions as
    | Array<{ data: any; period?: string; documentId?: string }>
    | undefined;
  const hasMultipleDocs = allExtractions && allExtractions.length > 1;

  if (hasMultipleDocs) {
    return (
      <Tabs defaultValue="doc-0" className="space-y-4">
        <TabsList>
          {allExtractions.map((ext, i) => (
            <TabsTrigger key={i} value={`doc-${i}`}>
              {ext.period || `Statement ${i + 1}`}
            </TabsTrigger>
          ))}
        </TabsList>
        {allExtractions.map((ext, i) => (
          <TabsContent key={i} value={`doc-${i}`}>
            <BankStatementTabContent bankData={ext.data} />
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  // Single document fallback (existing behavior)
  const bankData = analysis.aiAnalysis?.rawData?.bankStatementFindings
    || analysis.rawData?.bankStatementFindings
    || analysis.bankStatementData
    || analysis.bankStatementFindings;

  return <BankStatementTabContent bankData={bankData} />;
}

function BankStatementTabContent({ bankData }: { bankData: any }) {
  if (!bankData) {
    return (
      <Card className="p-16 text-center">
        <Banknote className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No Bank Statement Data</p>
        <p className="text-muted-foreground">Bank statement analysis will appear here after extraction.</p>
      </Card>
    );
  }

  const findings = bankData.findings || {};
  const categories = Object.keys(findings);

  return (
    <div className="space-y-6">
      {/* Account Info */}
      {bankData.accountInfo && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Account Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(bankData.accountInfo).map(([key, value]) => (
              <div key={key}>
                <p className="text-sm text-muted-foreground capitalize">
                  {humanLabel(key)}
                </p>
                <div className="font-medium"><SafeValue value={value} /></div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Summary */}
      {bankData.summary && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Summary</h3>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-muted-foreground">Overall Risk:</span>
            <RiskPill risk={bankData.summary.overallRisk} />
          </div>
          {bankData.summary.executiveSummary && (
            <p className="text-muted-foreground whitespace-pre-line">
              {bankData.summary.executiveSummary}
            </p>
          )}
        </Card>
      )}

      {/* Categories */}
      <Accordion type="multiple" className="space-y-2">
        {categories.map((category) => {
          const categoryData = findings[category];
          if (!categoryData) return null;

          return (
            <AccordionItem key={category} value={category} className="border rounded-lg">
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center gap-3">
                  <span className="capitalize">
                    {category.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                  {categoryData.riskScore && (
                    <RiskPill risk={categoryData.riskScore} />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <BankStatementCategoryContent data={categoryData} />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

// ===========================================
// Bank Statement Category Content
// ===========================================

// Keys that are handled separately (not rendered in the data fields loop)
const BS_META_KEYS = new Set([
  'riskScore', 'consistencyScore', 'qualitativeSummary', 'flaggedIssues',
]);

// Keys whose values are internal metadata and should be hidden from the UI
const BS_EVIDENCE_KEYS = new Set([
  'page', 'textSnippet', 'evidenceStatus', 'sourceDocument',
]);

/** Format a camelCase key into a readable label */
const bsLabel = (key: string) => key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();

/** Return true if a value looks like a risk/score string (green/yellow/red) */
const isRiskValue = (v: unknown): v is string =>
  typeof v === 'string' && ['green', 'yellow', 'red'].includes(v.toLowerCase());

/** Render a single value, filtering out evidence metadata objects */
function BsValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">N/A</span>;
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'string' || typeof value === 'number') {
    return isRiskValue(value) ? <RiskPill risk={value} /> : <span>{String(value)}</span>;
  }
  // Object that looks like evidence metadata — skip it
  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([k]) => !BS_EVIDENCE_KEYS.has(k));
    if (entries.length === 0) return null;
    return <span>{entries.map(([k, v]) => `${bsLabel(k)}: ${String(v ?? 'N/A')}`).join(', ')}</span>;
  }
  // Fallback for arrays or unexpected types — use SafeValue to avoid [object Object]
  return <SafeValue value={value} />;
}

function BankStatementCategoryContent({ data }: { data: Record<string, unknown> }) {
  // Separate fields into simple key-value pairs and complex (array/object) sections
  const simpleFields: [string, unknown][] = [];
  const complexFields: [string, unknown][] = [];

  for (const [key, value] of Object.entries(data)) {
    if (BS_META_KEYS.has(key) || value === null || value === undefined) continue;
    if (typeof value === 'object') {
      complexFields.push([key, value]);
    } else {
      simpleFields.push([key, value]);
    }
  }

  return (
    <div className="space-y-4">
      {/* Qualitative summary */}
      {typeof data.qualitativeSummary === 'string' && data.qualitativeSummary.length > 0 && (
        <p className="text-muted-foreground text-sm leading-relaxed">
          {data.qualitativeSummary}
        </p>
      )}

      {/* Flagged issues */}
      {Array.isArray(data.flaggedIssues) && data.flaggedIssues.length > 0 && (
        <div>
          <p className="font-medium text-sm mb-1 text-destructive">Flagged Issues:</p>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
            {data.flaggedIssues.map((issue: string, i: number) => (
              <li key={i}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Simple key-value fields (strings, numbers, booleans, risk scores) */}
      {simpleFields.length > 0 && (
        <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-sm">
          {simpleFields.map(([key, value]) => (
            <React.Fragment key={key}>
              <span className="text-muted-foreground">{bsLabel(key)}</span>
              <span className="font-medium text-right"><BsValue value={value} /></span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Complex fields (arrays of objects, nested objects) */}
      {complexFields.map(([key, value]) => {
        // Array of items (loan payments, deposit sources, etc.)
        if (Array.isArray(value)) {
          if (value.length === 0) return null;
          // Array of plain strings
          if (value.every(v => typeof v === 'string')) {
            return (
              <div key={key}>
                <p className="font-medium text-sm mb-2">{bsLabel(key)}</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
                  {value.map((item, i) => <li key={i}>{String(item)}</li>)}
                </ul>
              </div>
            );
          }
          // Array of objects — render each as a card-like row
          return (
            <div key={key}>
              <p className="font-medium text-sm mb-2">{bsLabel(key)}</p>
              <div className="space-y-2">
                {value.map((item, i) => {
                  if (typeof item !== 'object' || item === null) {
                    return <p key={i} className="text-sm text-muted-foreground">{String(item)}</p>;
                  }
                  const entries = Object.entries(item as Record<string, unknown>)
                    .filter(([k]) => !BS_EVIDENCE_KEYS.has(k));
                  if (entries.length === 0) return null;
                  return (
                    <div key={i} className="rounded-md border bg-muted/30 px-4 py-2.5 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
                      {entries.map(([k2, v2]) => (
                        <React.Fragment key={k2}>
                          <span className="text-muted-foreground">{bsLabel(k2)}</span>
                          <span className="font-medium text-right"><BsValue value={v2} /></span>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Plain object (netCashFlow, totalDeposits, etc.)
        const obj = value as Record<string, unknown>;
        const entries = Object.entries(obj).filter(([k]) => !BS_EVIDENCE_KEYS.has(k));
        if (entries.length === 0) return null;
        return (
          <div key={key}>
            <p className="font-medium text-sm mb-2">{bsLabel(key)}</p>
            <div className="rounded-md border bg-muted/30 px-4 py-2.5 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
              {entries.map(([k2, v2]) => (
                <React.Fragment key={k2}>
                  <span className="text-muted-foreground">{bsLabel(k2)}</span>
                  <span className="font-medium text-right"><BsValue value={v2} /></span>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RiskPill({ risk }: { risk: string }) {
  const colors = {
    green: 'bg-green-500/20 text-green-700 border-green-500/50',
    yellow: 'bg-yellow-500/20 text-yellow-700 border-yellow-500/50',
    red: 'bg-red-500/20 text-red-700 border-red-500/50',
  };

  const labels = {
    green: 'Low Risk',
    yellow: 'Moderate Risk',
    red: 'High Risk',
  };

  return (
    <span
      className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full border',
        colors[risk as keyof typeof colors] || 'bg-muted text-muted-foreground'
      )}
    >
      {labels[risk as keyof typeof labels] || risk}
    </span>
  );
}

function EmptyState({ title, icon: Icon }: { title: string; icon: any }) {
  return (
    <Card className="p-16 text-center">
      <Icon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
      <p className="text-xl font-medium mb-2">{title}</p>
      <p className="text-muted-foreground">
        Corporate Search Analysis was not part of this workflow.
      </p>
    </Card>
  );
}

// ===========================================
// Corporate Tab
// ===========================================

function CorporateTab({ analysis }: { analysis: any }) {
  // Check for multi-document extractions
  const allExtractions = analysis.aiAnalysis?.allCorporateExtractions as
    | Array<{ data: any; period?: string; documentId?: string }>
    | undefined;
  const hasMultipleDocs = allExtractions && allExtractions.length > 1;

  if (hasMultipleDocs) {
    return (
      <Tabs defaultValue="doc-0" className="space-y-4">
        <TabsList>
          {allExtractions.map((ext, i) => (
            <TabsTrigger key={i} value={`doc-${i}`}>
              {ext.period || `Search ${i + 1}`}
            </TabsTrigger>
          ))}
        </TabsList>
        {allExtractions.map((ext, i) => (
          <TabsContent key={i} value={`doc-${i}`}>
            <CorporateTabContent accountData={ext.data} />
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  // Single document fallback (existing behavior)
  const accountData =
    analysis?.aiAnalysis?.rawData?.accountData ||
    analysis?.rawData?.accountData ||
    analysis?.accountData;

  return <CorporateTabContent accountData={accountData} />;
}

function CorporateTabContent({ accountData }: { accountData: any }) {
  if (!accountData) return <EmptyState title="No Corporate Data" icon={Building2} />;

  const validEntries = Object.entries(accountData).filter(([, value]) =>
    value !== null && value !== undefined && value !== '' && value !== 'null'
  );

  return (
    <Card className="p-6">
      <h3 className="font-semibold mb-6">Corporate Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {validEntries.map(([key, value]) => (
          <div key={key} className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground capitalize mb-1">
              {humanLabel(key)}
            </p>
            <div className="font-medium"><SafeValue value={value} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ===========================================
// Research Findings Section (inside AI Analysis tab)
// ===========================================

function ResearchFindingsSection({ research }: { research: any }) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  if (!research) return null;

  // Support both new full-structured format and old flat format
  const adverseMedia = research.adverseMedia || [];
  const businessLegitimacy = research.businessLegitimacy;
  const industryOutlook = research.industryOutlook;
  const industryBenchmarks = research.industryBenchmarks;
  const principalBackground = research.principalBackground || [];

  // Old format only had adverseFindings (string[]) and industryContext (string)
  const isOldFormat = !research.adverseMedia && !research.businessLegitimacy;

  // If old format with minimal data, show a simple card
  if (isOldFormat) {
    const hasAnyOldData = research.adverseFindings?.length > 0 || research.industryContext || research.businessLegitimacy;
    if (!hasAnyOldData) return null;
    return (
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-primary" />
          External Research
        </h3>
        {research.industryContext && (
          <p className="text-sm text-muted-foreground mb-3">{research.industryContext}</p>
        )}
        {typeof research.businessLegitimacy === 'string' && research.businessLegitimacy && (
          <p className="text-sm text-muted-foreground mb-3">{research.businessLegitimacy}</p>
        )}
        {research.adverseFindings?.length > 0 && (
          <div className="space-y-1">
            {research.adverseFindings.map((f: string, i: number) => (
              <p key={i} className="text-sm text-muted-foreground">{f}</p>
            ))}
          </div>
        )}
      </Card>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const hasContent = adverseMedia.length > 0 || businessLegitimacy || industryOutlook || industryBenchmarks || principalBackground.length > 0;
  if (!hasContent) return null;

  const directionColors: Record<string, string> = {
    growing: 'bg-green-500/10 text-green-600 border-green-500/20',
    stable: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    contracting: 'bg-red-500/10 text-red-600 border-red-500/20',
    uncertain: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  };

  const severityColors: Record<string, string> = {
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-blue-500',
  };

  return (
    <Card className="p-6">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Globe className="w-5 h-5 text-primary" />
        Research Findings
        {research.confidenceScore != null && (
          <span className="ml-auto text-xs text-muted-foreground font-normal">
            Confidence: {Math.round(research.confidenceScore * 100)}%
          </span>
        )}
      </h3>

      <div className="space-y-3">

        {/* Adverse Media */}
        {adverseMedia.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('adverse')}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium text-sm">
                <AlertTriangle className="w-4 h-4 text-warning" />
                Adverse Media ({adverseMedia.length} finding{adverseMedia.length !== 1 ? 's' : ''})
              </span>
              {expandedSections.has('adverse') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expandedSections.has('adverse') && (
              <div className="px-3 pb-3 space-y-2">
                {adverseMedia.map((item: any, i: number) => (
                  <div key={i} className="p-3 bg-muted/30 rounded-lg text-sm">
                    <div className="flex items-start gap-2">
                      <span className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', severityColors[item.severity] || 'bg-gray-400')} />
                      <div className="flex-1 min-w-0">
                        {item.title && (
                          <p className="font-semibold mb-1">{item.title}</p>
                        )}
                        <p>{item.finding}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          {item.date && <span>{item.date}</span>}
                          {item.source && <span>{item.source}</span>}
                          {item.sourceUrl && (
                            <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline truncate">
                              <ExternalLink className="w-3 h-3 shrink-0" /> Source
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No adverse media — positive signal */}
        {adverseMedia.length === 0 && (
          <div className="flex items-center gap-2 p-3 bg-green-500/5 border border-green-500/20 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            No adverse media found
          </div>
        )}

        {/* Business Legitimacy */}
        {businessLegitimacy && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('legitimacy')}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium text-sm">
                <ShieldCheck className="w-4 h-4 text-primary" />
                Business Legitimacy
              </span>
              {expandedSections.has('legitimacy') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expandedSections.has('legitimacy') && (
              <div className="px-3 pb-3">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { label: 'Web Presence', value: businessLegitimacy.hasWebPresence },
                    { label: 'Reviews', value: businessLegitimacy.hasReviews },
                    { label: 'LinkedIn', value: businessLegitimacy.hasLinkedIn },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-1.5 text-sm p-2 bg-muted/30 rounded">
                      {check.value
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <span className={check.value ? '' : 'text-muted-foreground'}>{check.label}</span>
                    </div>
                  ))}
                </div>
                {businessLegitimacy.assessment && (
                  <p className="text-sm text-muted-foreground">{businessLegitimacy.assessment}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Industry Outlook */}
        {industryOutlook && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('industry')}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium text-sm">
                <TrendingUp className="w-4 h-4 text-primary" />
                Industry Outlook
                {industryOutlook.direction && (
                  <span className={cn('text-xs px-2 py-0.5 rounded-full border', directionColors[industryOutlook.direction] || 'bg-muted')}>
                    {industryOutlook.direction}
                  </span>
                )}
              </span>
              {expandedSections.has('industry') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expandedSections.has('industry') && (
              <div className="px-3 pb-3 space-y-2">
                {industryOutlook.summary && (
                  <p className="text-sm text-muted-foreground">{industryOutlook.summary}</p>
                )}
                {industryOutlook.keyTrends?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Key Trends</p>
                    {industryOutlook.keyTrends.map((trend: string, i: number) => (
                      <p key={i} className="text-sm text-muted-foreground pl-3 border-l-2 border-primary/20">{trend}</p>
                    ))}
                  </div>
                )}
                {industryOutlook.regulatoryConsiderations && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Regulatory</p>
                    <p className="text-sm text-muted-foreground">{industryOutlook.regulatoryConsiderations}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Industry Benchmarks */}
        {industryBenchmarks && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('benchmarks')}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium text-sm">
                <FileBarChart className="w-4 h-4 text-primary" />
                Industry Benchmarks
              </span>
              {expandedSections.has('benchmarks') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expandedSections.has('benchmarks') && (
              <div className="px-3 pb-3">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Gross Margin', value: industryBenchmarks.typicalGrossMargin },
                    { label: 'Debt-to-Equity', value: industryBenchmarks.typicalDebtToEquity },
                    { label: 'Current Ratio', value: industryBenchmarks.typicalCurrentRatio },
                  ].filter(b => b.value).map((benchmark) => (
                    <div key={benchmark.label} className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-xs text-muted-foreground">{benchmark.label}</p>
                      <p className="text-sm font-semibold mt-0.5">{benchmark.value}</p>
                    </div>
                  ))}
                </div>
                {industryBenchmarks.source && (
                  <p className="text-xs text-muted-foreground mt-2">Source: {industryBenchmarks.source}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Principal Background */}
        {principalBackground.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('principals')}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium text-sm">
                <Shield className="w-4 h-4 text-primary" />
                Principal Background ({principalBackground.length})
              </span>
              {expandedSections.has('principals') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expandedSections.has('principals') && (
              <div className="px-3 pb-3 space-y-2">
                {principalBackground.map((p: any, i: number) => (
                  <div key={i} className="p-3 bg-muted/30 rounded-lg text-sm">
                    <p className="font-medium mb-1">{p.name}</p>
                    <p className="text-muted-foreground">{p.findings}</p>
                    {p.sourceUrls?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {p.sourceUrls.map((url: string, j: number) => (
                          <a key={j} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                            <ExternalLink className="w-3 h-3" /> Source {j + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </Card>
  );
}

// ===========================================
// AI Analysis Tab
// ===========================================

function AIAnalysisTab({ analysis }: { analysis: any }) {
  const ai = analysis.aiAnalysis;
  const executiveSummary = ai?.executiveSummary;
  const ratios = ai?.findings?.financialRatios || [];
  const creditItems = ai?.findings?.creditReportItems || ai?.findings?.equifaxItems || [];
  const accountItems = ai?.findings?.accountItems || [];

  if (!ai || (!executiveSummary && ratios.length === 0)) {
    return (
      <Card className="p-16 text-center">
        <TrendingUp className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No AI analysis available</p>
        <p className="text-muted-foreground">Run the analysis to generate AI insights.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Executive Summary */}
      {executiveSummary && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Executive Summary
          </h3>
          <div className="prose prose-sm max-w-none">
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
              {executiveSummary}
            </p>
          </div>
        </Card>
      )}

      {/* Financial Ratios Analysis */}
      {ratios.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-success" />
            Financial Ratios Analysis
          </h3>
          <div className="space-y-4">
            {ratios.map((ratio: any, i: number) => (
              <div key={i} className="p-4 bg-muted/50 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{ratio.ratioName}</span>
                  <span className="text-lg font-semibold text-primary">
                    {ratio.calculatedValue}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  <span className="font-medium">Formula:</span> {ratio.formula}
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  <span className="font-medium">Calculation:</span> {ratio.calculation}
                </p>
                <p className="text-sm mt-2">{ratio.interpretation}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Credit Report Findings */}
      {creditItems.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-warning" />
            Credit Bureau Findings
          </h3>
          <div className="space-y-3">
            {creditItems.map((item: any, i: number) => (
              <div key={i} className="p-4 bg-muted/50 rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{item.itemName}</span>
                  <span className="text-sm bg-muted px-2 py-0.5 rounded">{item.category}</span>
                </div>
                <p className="text-lg font-semibold">{item.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{item.significance}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Corporate/Account Findings */}
      {accountItems.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Corporate Search Findings
          </h3>
          <div className="space-y-3">
            {accountItems.map((item: any, i: number) => (
              <div key={i} className="p-4 bg-muted/50 rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{item.itemName}</span>
                  <span className="text-sm bg-muted px-2 py-0.5 rounded">{item.category}</span>
                </div>
                <p className="text-muted-foreground">{item.value}</p>
                {item.significance && (
                  <p className="text-sm text-muted-foreground mt-1">{item.significance}</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Research Findings */}
      <ResearchFindingsSection research={ai?.findings?.externalResearch} />
    </div>
  );
}

// ===========================================
// Documents Tab
// ===========================================

function DocumentsTab({ analysis }: { analysis: any }) {
  const [expandedIntegrity, setExpandedIntegrity] = useState<Set<string>>(new Set());

  // Get document IDs associated with this analysis
 const analysisDocumentIds: string[] = useMemo(() => {
    const ids = new Set<string>();

    // Check DB array path
    if (analysis?.aiAnalysis?.documentIds) {
      analysis.aiAnalysis.documentIds.forEach((id: string) => ids.add(id));
    }

    if (analysis?.financialUploadId) ids.add(analysis.financialUploadId);
    if (analysis?.creditReportUploadId) ids.add(analysis.creditReportUploadId);
    if (analysis?.equifaxUploadId) ids.add(analysis.equifaxUploadId); // legacy field
    if (analysis?.bankStatementUploadId) ids.add(analysis.bankStatementUploadId);
    if (analysis?.corporateUploadId) ids.add(analysis.corporateUploadId);

    return Array.from(ids);
  }, [analysis]);

  const { data: documents, isLoading } = useQuery({
    // Adding  sorted IDs to the key ensures a fresh fetch if the IDs list changes
    queryKey: ['documents', analysisDocumentIds.sort().join(',')],
    queryFn: () => api.documents.list(),
    enabled: analysisDocumentIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch integrity reports for all analysis documents
  const { data: integrityData } = useQuery({
    queryKey: ['integrity-reports', analysisDocumentIds.sort().join(',')],
    queryFn: () => api.documents.integrityReports(analysisDocumentIds),
    enabled: analysisDocumentIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const integrityReports: Record<string, any> = integrityData?.data || {};

  // Filter documents to only those used in this analysis
  const analysisDocuments = useMemo(() => {
    return (documents?.data || []).filter(
      (doc: any) => analysisDocumentIds.includes(doc.id)
    );
  }, [documents, analysisDocumentIds]);

  const toggleIntegrity = (docId: string) => {
    setExpandedIntegrity(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  if (analysisDocumentIds.length === 0) {
    return (
      <Card className="p-16 text-center">
        <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">No Documents Linked</p>
        <p className="text-muted-foreground">
          No documents are associated with this analysis.
          <br />
          Documents uploaded in future reports will appear here.
        </p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="skeleton h-8 w-48 mb-4" />
        <div className="space-y-3">
          <div className="skeleton h-16 rounded-lg" />
          <div className="skeleton h-16 rounded-lg" />
        </div>
      </Card>
    );
  }

  if (analysisDocuments.length === 0) {
    return (
      <Card className="p-16 text-center">
        <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-xl font-medium mb-2">Documents Not Found</p>
        <p className="text-muted-foreground">
          The linked documents may have been deleted or are no longer available.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="font-semibold mb-4">Uploaded Documents ({analysisDocuments.length})</h3>
      <div className="space-y-3">
        {analysisDocuments.map((doc: any) => {
          const report = integrityReports[doc.id];
          const isExpanded = expandedIntegrity.has(doc.id);
          const flags: Array<{ category: string; severity: string; message: string; field?: string }> = report?.flags || [];

          return (
            <div key={doc.id} className="bg-muted rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{doc.originalName}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{doc.documentType?.replace('_', ' ')}</span>
                      <span>•</span>
                      <span>{formatFileSize(doc.fileSize)}</span>
                      <span>•</span>
                      <span>{doc.status}</span>
                      {report && (
                        <>
                          <span>•</span>
                          <button
                            onClick={() => flags.length > 0 && toggleIntegrity(doc.id)}
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                              report.verdict === 'clean' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                              report.verdict === 'warning' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 cursor-pointer',
                              report.verdict === 'alert' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 cursor-pointer',
                            )}
                          >
                            {report.verdict === 'clean' && <ShieldCheck className="w-3 h-3" />}
                            {report.verdict === 'warning' && <AlertTriangle className="w-3 h-3" />}
                            {report.verdict === 'alert' && <ShieldAlert className="w-3 h-3" />}
                            {report.verdict === 'clean' ? 'Clean' : `${flags.length} flag${flags.length !== 1 ? 's' : ''}`}
                            {flags.length > 0 && (
                              <ChevronRight className={cn('w-3 h-3 transition-transform', isExpanded && 'rotate-90')} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-success" />
                  ) : doc.status === 'failed' ? (
                    <AlertCircle className="w-5 h-5 text-destructive" />
                  ) : (
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  )}
                  <a
                    href={api.documents.download(doc.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-background rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>
              {isExpanded && flags.length > 0 && (
                <div className="px-4 pb-4 pt-0">
                  <div className="bg-background/50 rounded-md p-3 space-y-2">
                    {flags.map((flag: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className={cn(
                          'mt-0.5 w-2 h-2 rounded-full shrink-0',
                          flag.severity === 'high' && 'bg-red-500',
                          flag.severity === 'medium' && 'bg-amber-500',
                          flag.severity === 'low' && 'bg-blue-400',
                        )} />
                        <span className="text-muted-foreground">{flag.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ===========================================
// Utility Components
// ===========================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium capitalize">{value}</span>
    </div>
  );
}

function MetricCard({
  label,
  value,
  benchmark,
  inverse = false,
  suffix = '',
}: {
  label: string;
  value: string | null;
  benchmark: number;
  inverse?: boolean;
  suffix?: string;
}) {
  const numValue = value ? parseFloat(value) : null;
  const isGood = numValue
    ? inverse
      ? numValue <= benchmark
      : numValue >= benchmark
    : false;

  return (
    <div className="p-4 bg-muted rounded-lg">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={cn('text-2xl font-bold', numValue ? (isGood ? 'text-success' : 'text-warning') : '')}>
        {numValue ? formatNumber(numValue) + suffix : 'N/A'}
      </p>
    </div>
  );
}

function RatioRow({
  label,
  value,
  format = 'number',
  benchmark,
  suffix = '',
}: {
  label: string;
  value: string | null;
  format?: 'number' | 'percent' | 'currency';
  benchmark?: string;
  suffix?: string;
}) {
  let displayValue = 'N/A';
  if (value) {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      displayValue =
        format === 'percent'
          ? formatPercent(num)
          : format === 'currency'
          ? `$${formatNumber(num, 0)}`
          : formatNumber(num) + suffix;
    }
  }

  return (
    <div className="flex justify-between items-center">
      <div>
        <span className="text-sm">{label}</span>
        {benchmark && <span className="text-xs text-muted-foreground ml-2">({benchmark})</span>}
      </div>
      <span className="font-mono font-medium">{displayValue}</span>
    </div>
  );
}

// ===========================================
// Overrides Tab
// ===========================================

function OverridesTab({ workflowId, analysisId }: { workflowId?: string; analysisId?: string }) {
  const [overrides, setOverrides] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchOverrides() {
      // Need at least one identifier to query overrides
      if (!workflowId && !analysisId) {
        setLoading(false);
        return;
      }
      try {
        const query = workflowId ? { workflowId } : { analysisId: analysisId! };
        const res = await api.overrides.list(query);
        setOverrides(res.data || []);
      } catch {
        // If no overrides found or IDs don't match, that's OK
        setOverrides([]);
      } finally {
        setLoading(false);
      }
    }
    fetchOverrides();
  }, [workflowId, analysisId]);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading overrides...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <OverrideAuditDisplay overrides={overrides} />
    </Card>
  );
}
